import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import image1 from "../../Assets/Images/seekjob/imageo.png";
import image2 from "../../Assets/Images/seekjob/imagethree.png";
import image3 from "../../Assets/Images/seekjob/imagetwo.png";
import image4 from "../../Assets/Images/seekjob/imagefour.png";

// Typewriter hook remains unchanged
const useSegmentedTypewriter = (segments, speed = 30, start = false) => {
    const [charIndex, setCharIndex] = useState(0);
    const [displayed, setDisplayed] = useState([]);
    const flatRef = useRef([]);

    useEffect(() => {
        flatRef.current = segments.flatMap(({ text, color }) =>
            text.split("").map((char) => ({ char, color }))
        );
    }, [segments]);

    useEffect(() => {
        if (!start) return;

        const flat = flatRef.current;

        const interval = setInterval(() => {
            setCharIndex((prevIndex) => {
                if (prevIndex < flat.length) {
                    setDisplayed((prev) => [...prev, flat[prevIndex]]);
                    return prevIndex + 1;
                } else {
                    clearInterval(interval);
                    return prevIndex;
                }
            });
        }, speed);

        return () => clearInterval(interval);
    }, [start, speed]);

    return displayed;
};

const aiCards = [
    {
        id: 1,
        title: "Resume Optimization",
        imgSrc: image3,
        caption: "Beat 95% of ATS filters",
    },
    {
        id: 2,
        title: "Job Intelligence",
        imgSrc: image2,
        caption: "3x faster applications with perfect matches",
    },
    {
        id: 3,
        title: "Skill Analysis",
        imgSrc: image4,
        caption: "Instant gap identification and closing strategies",
    },
    {
        id: 4,
        title: "Network Mapping",
        imgSrc: image1,
        caption: "Find the right people at the right companies",
    },
];

function BehindAI() {
    const [startTyping, setStartTyping] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setStartTyping(true);
                }
            },
            {
                threshold: 0.3,
            }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const segments = [
        { text: "Our AI", color: "white" },
        { text: " doesn’t just match keywords— ", color: "default" },
        { text: "it understands your potential.", color: "white" },
        {
            text:
                " It analyzes skills, goals, and\ncareer trajectory to serve up opportunities that make sense for your journey.",
            color: "default",
        },
    ];

    const typedCharacters = useSegmentedTypewriter(segments, 30, startTyping);

    const renderStyledCharacters = () =>
        typedCharacters.map((item, idx) => {
            let content = item.char;
            if (content === " ") content = "\u00A0";
            if (content === "\n") return <br key={idx} />;
            return (
                <span
                    key={idx}
                    className={
                        item.color === "white" ? "tw-text-white" : "tw-text-[#878787]"
                    }
                >
                    {content}
                </span>
            );
        });

    const animationVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: (i = 1) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.2,
                duration: 0.6,
                ease: "easeOut",
            },
        }),
    };

    return (
        <section
            ref={sectionRef}
            className="tw-container tw-mx-auto tw-text-center tw-relative tw-z-10 tw-mt-[100px] tw-text-white"
        >
            <motion.h2
                className="tw-text-[36px] sm:tw-text-[66px] tw-font-semibold tw-mb-4 tw-text-center"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={animationVariants}
            >
                Behind Our AI
            </motion.h2>

            <motion.p
                className="tw-font-bold tw-text-[16px] sm:tw-text-[21px] tw-mb-10 tw-text-center tw-whitespace-pre-line tw-px-2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={animationVariants}
                custom={1}
            >
                {renderStyledCharacters()}
            </motion.p>

            <div className="tw-flex tw-gap-3 tw-px-2 tw-overflow-x-auto tw-scrollbar-hide sm:tw-flex-wrap sm:tw-justify-center sm:tw-overflow-visible">
                {aiCards.map(({ id, title, imgSrc, caption }, index) => (
                    <motion.div
                        key={id}
                        className="tw-flex-shrink-0 sm:tw-flex-shrink tw-w-[270px] tw-flex tw-flex-col tw-items-center tw-justify-start tw-transition tw-duration-300"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={animationVariants}
                        custom={index + 2}
                    >
                        <h4 className="tw-text-[18px] tw-font-semibold tw-mb-2 tw-text-center">
                            {title}
                        </h4>

                        <div className="tw-overflow-hidden tw-rounded tw-w-full tw-h-[400px]">
                            <div className="tw-flex tw-items-center tw-justify-center tw-rounded tw-w-full tw-h-[400px]">
                                <img
                                    src={imgSrc}
                                    alt={title}
                                    className="tw-max-w-full tw-max-h-full tw-object-contain"
                                    loading="lazy"
                                />
                            </div>

                        </div>

                        <p className="tw-text-[17px] tw-text-white tw-mt-2 tw-text-center tw-leading-tight">
                            {caption}
                        </p>
                    </motion.div>
                ))}
            </div>

        </section>
    );
}

export default BehindAI;
